import { FC, ReactNode, useMemo } from 'react';
import {
  CtaFeatureSlice,
  CtaFeatureSliceCenteredButton,
  CtaFeatureSliceDefault,
  CtaFeatureSliceDefaultItem,
  CtaFeatureSliceImageWithLogoAndButton,
  CtaFeatureSliceImageWithTitleAndButton,
  CtaFeatureSliceImageWithTwoCta,
  CtaFeatureSliceTitleWithCta,
  CtaFeatureSliceTitleWithTwoLinks,
} from '@/types';
import * as prismicH from '@prismicio/helpers';

import {
  convertHexadecimalFormatToRGB,
  isCurrentDateInRange,
  toZonedDate,
} from '@/utils';

import {
  Button,
  Container,
  IconSvg,
  Link,
  PrismicImageWithAlt,
} from '@/components';
import classNames from 'classnames';
import { htmlSerializerWithProps } from '@/utils';
import { PrismicLink, PrismicRichText } from '@prismicio/react';
import {
  ColorField,
  EmptyImageFieldImage,
  FilledImageFieldImage,
  LinkField,
} from '@prismicio/client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { useRouter } from 'next/navigation';
import { useIsMobile, useSelectedStore } from '@/hooks';
import { format } from 'date-fns';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import styles from './CtaFeature.module.scss';

const RenderCta: FC<CtaFeatureSlice> = slice => {
  if (slice.variation === 'titleWithTwoLinks') {
    return <TitleWithTwoLinksVariation slice={slice} />;
  }

  if (slice.variation === 'titleWithCta') {
    return <TitleWithCtaVariation slice={slice} />;
  }

  if (slice.variation === 'centeredButton') {
    return <CenteredButtonVariation slice={slice} />;
  }

  if (slice.variation === 'imageWithTwoCta') {
    return <ImageWithTwoCtaVariation slice={slice} />;
  }

  if (slice.variation === 'imageWithTitleAndButton') {
    return <ImageWithTitleAndButtonVariation slice={slice} />;
  }

  if (slice.variation === 'imageWithLogoAndButton') {
    return <ImageWithLogoAndButtonVariation slice={slice} />;
  }

  return <DefaultVariation slice={slice} />;
};

const CenteredButtonVariation: FC<{
  slice: CtaFeatureSliceCenteredButton & CtaFeatureSlice;
}> = ({ slice }) => {
  const {
    cta_type,
    custom_cta_background,
    custom_cta_border_color,
    custom_cta_text_color,
    custom_cta_background_hover,
    custom_cta_border_color_hover,
    custom_cta_text_color_hover,
    external_link,
    internal_link,
    cta_text,
    fit_cta_button_width_to_content,
  } = slice?.primary ?? {};

  return (
    <CTAButton
      className={styles.cta__action_button}
      ctaType={cta_type}
      customStyles={
        cta_type === 'custom'
          ? {
              background: custom_cta_background,
              border_color: custom_cta_border_color,
              text_color: custom_cta_text_color,
              background_hover: custom_cta_background_hover,
              border_color_hover: custom_cta_border_color_hover,
              text_color_hover: custom_cta_text_color_hover,
            }
          : undefined
      }
      external={external_link}
      fitContentWidth={fit_cta_button_width_to_content}
      link={internal_link ?? ''}
    >
      {cta_text}
    </CTAButton>
  );
};

const DefaultVariation: FC<{
  slice: CtaFeatureSliceDefault & CtaFeatureSlice;
}> = ({ slice }) => {
  const { store: currentStore } = useSelectedStore();

  const filterCTAItems =
    (currentStoreUrl?: string) => (item: CtaFeatureSliceDefaultItem) => {
      const { location_slug: slug, start_date: start, end_date: end } = item;
      const currentDay = format(toZonedDate(), 'eeee');
      const isItemDayValid = [null, 'Everyday', currentDay].includes(item?.day);
      const isItemDateInRange = isCurrentDateInRange(start, end);
      const isItemLocationValid = [null, currentStoreUrl].includes(slug);

      return isItemDayValid && isItemDateInRange && isItemLocationValid;
    };

  const ctaItems = useMemo(
    () => slice?.items.filter(filterCTAItems(currentStore?.url_slug)),
    [slice?.items, currentStore?.url_slug],
  );

  if (!ctaItems?.length) {
    return null;
  }

  const primary = slice?.primary;
  const autoplayConfig = {
    delay: 5000,
    disableOnInteraction: true,
    stopOnLastSlide: false,
  };
  const useAutoPlay = primary?.enable_autoplay ? autoplayConfig : false;

  const fitContentWidth = primary?.fit_cta_button_width_to_content;

  const CTAContent = ({ item }: { item: CtaFeatureSliceDefaultItem }) => {
    const maxWidthHeader = item?.max_width_header
      ? `${item?.max_width_header}px`
      : '100%';
    const maxWidthBlurb = item?.max_width_blurb
      ? `${item?.max_width_blurb}px`
      : '100%';
    const borderRadius = primary?.cta_full_width
      ? '0px'
      : `${primary?.image_border_radius}px`;

    const background = item?.content_background_color
      ? convertHexadecimalFormatToRGB(item?.content_background_color)
      : null;

    const ctaBanner = (
      <>
        <figure
          className={classNames(styles.banner__horizontal, {
            [styles.banner__horizontal_not_placeholder]: item?.show_content,
          })}
          style={{
            borderRadius,
            paddingTop: getApectRatio(
              item?.image?.dimensions?.width as number,
              item?.image?.dimensions?.height as number,
              primary?.percentage_respect_width as number,
              false,
            ),
          }}
        >
          {<RenderImage borderRadius={borderRadius} fill image={item?.image} />}
        </figure>
        <figure
          className={classNames(styles.banner__vertical, {
            [styles.banner__vertical_not_placeholder]: item?.show_content,
          })}
          style={{
            paddingTop: getApectRatio(
              item?.image?.mobile?.dimensions?.width as number,
              item?.image?.mobile?.dimensions?.height as number,
              primary?.percentage_respect_to_width_mobile as number,
              true,
            ),
          }}
        >
          {<RenderImage borderRadius={'0px'} fill image={item?.image.mobile} />}
        </figure>
      </>
    );
    return (
      <>
        {item?.show_content && (
          <Container className={styles.info_container}>
            <div
              className={classNames(styles.info, {
                [styles.info__text_mobile_left_aligned]:
                  item?.text_mobile_aligned_left,
                [styles.info_align_left]: item.content_align === 'left',
                [styles.info_align_right]: item.content_align === 'right',
                [styles.info_align_center]:
                  item.content_align === 'center' || !item.content_align,
                [styles.info_full_width]: item?.max_width_header,
              })}
              style={{
                backgroundColor: item?.content_background_color
                  ? `rgba(${background?.r}, ${background?.g}, ${
                      background?.b
                    }, ${item?.content_background_opacity ?? 1})`
                  : undefined,
              }}
            >
              {renderTextSlice(
                item?.header,
                item?.header_color ?? '',
                maxWidthHeader,
              )}
              {renderTextSlice(
                item?.blurb,
                item?.blurb_color ?? '',
                maxWidthBlurb,
              )}
              <div className={styles.cta_buttons_container}>
                {item?.cta_text && (
                  <CTAButton
                    className={styles.cta__action_button}
                    ctaType={item?.cta_type}
                    customStyles={
                      item?.cta_type === 'custom'
                        ? {
                            background: item?.custom_cta_background,
                            border_color: item?.custom_cta_border_color,
                            text_color: item?.custom_cta_text_color,
                            background_hover: item?.custom_cta_background_hover,
                            border_color_hover:
                              item?.custom_cta_border_color_hover,
                            text_color_hover: item?.custom_cta_text_color_hover,
                          }
                        : undefined
                    }
                    external={item?.external_link}
                    fitContentWidth={fitContentWidth}
                    link={item?.link ?? ''}
                  >
                    {item?.cta_text}
                  </CTAButton>
                )}
                {item?.secondary_cta_text && (
                  <CTAButton
                    className={styles.cta__action_button}
                    ctaType={item?.secondary_cta_type}
                    customStyles={
                      item?.secondary_cta_type === 'custom'
                        ? {
                            background: item?.custom_secondary_cta_background,
                            border_color:
                              item?.custom_secondary_cta_border_color,
                            text_color: item?.custom_secondary_cta_text_color,
                            background_hover:
                              item?.custom_secondary_cta_bg_hover,
                            border_color_hover:
                              item?.custom_secondary_cta_border_hover,
                            text_color_hover:
                              item?.custom_secondary_cta_text_hover,
                          }
                        : undefined
                    }
                    external={item?.secondary_cta_external_link}
                    fitContentWidth={fitContentWidth}
                    link={item?.secondary_cta_link ?? ''}
                  >
                    {item?.secondary_cta_text}
                  </CTAButton>
                )}
              </div>
            </div>
          </Container>
        )}
        {(item?.link || item?.external_link?.link_type === 'Any') && (
          <Link
            aria-label={item?.external_link?.text || 'Go to the promotion link'}
            className={styles.cta__image_link}
            href={item?.link || '/'}
          >
            {ctaBanner}
            {item?.apply_effect_to_cta && item?.image?.url && (
              <div className={styles.cta__overlay} />
            )}
          </Link>
        )}
        {!item?.link && item?.external_link?.link_type !== 'Any' && (
          <PrismicLink
            aria-label={item?.external_link?.text || 'Go to the promotion link'}
            className={styles.cta__image_link}
            field={item?.external_link}
          >
            {ctaBanner}
            {item?.apply_effect_to_cta && item?.image?.url && (
              <div className={styles.cta__overlay} />
            )}
          </PrismicLink>
        )}
      </>
    );
  };

  return (
    <Swiper
      autoplay={useAutoPlay}
      className={styles.swipper__cta_carousel}
      effect="fade"
      loop
      modules={[Autoplay, EffectFade, Navigation, Pagination]}
      pagination={{
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
            index + 1
          }"></span>`,
      }}
      slidesPerView={1}
    >
      {ctaItems.map((cta, index) => (
        <SwiperSlide key={index}>
          <CTAContent item={cta} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const ImageWithTwoCtaVariation: FC<{
  slice: CtaFeatureSliceImageWithTwoCta & CtaFeatureSlice;
}> = ({ slice }) => {
  const titleColor = slice?.primary?.title_color;
  const image = slice?.primary?.image;
  const align = slice?.primary?.content_align;

  return (
    <>
      <figure className={styles.image_section}>
        {<RenderImage image={image} />}
      </figure>
      <div
        className={classNames(styles.content_section, {
          [styles.content_section__align_left]: align === 'left',
        })}
      >
        <PrismicRichText
          components={htmlSerializerWithProps({ color: titleColor })}
          field={slice?.primary?.title}
        />
        <div className={styles.cta_section}>
          {renderCta(
            slice?.primary?.first_cta_link,
            slice?.primary?.first_cta_image,
          )}
          {renderCta(
            slice?.primary?.second_cta_link,
            slice?.primary?.second_cta_image,
          )}
        </div>
      </div>
    </>
  );
};

const TitleWithCtaVariation: FC<{
  slice: CtaFeatureSliceTitleWithCta & CtaFeatureSlice;
}> = ({ slice }) => {
  const {
    title_color,
    cta_type,
    title,
    custom_cta_background,
    custom_cta_border_color,
    custom_cta_text_color,
    custom_cta_background_hover,
    custom_cta_border_color_hover,
    custom_cta_text_color_hover,
    external_link,
    internal_link,
    button_text,
    fit_cta_button_width_to_content,
  } = slice?.primary ?? {};

  return (
    <>
      <div className={styles.title}>
        <PrismicRichText
          components={htmlSerializerWithProps({ color: title_color })}
          field={title}
        />
      </div>
      <CTAButton
        ctaType={cta_type}
        customStyles={
          cta_type === 'custom'
            ? {
                background: custom_cta_background,
                border_color: custom_cta_border_color,
                text_color: custom_cta_text_color,
                background_hover: custom_cta_background_hover,
                border_color_hover: custom_cta_border_color_hover,
                text_color_hover: custom_cta_text_color_hover,
              }
            : undefined
        }
        external={external_link}
        fitContentWidth={fit_cta_button_width_to_content}
        link={internal_link ?? ''}
      >
        {button_text}
      </CTAButton>
    </>
  );
};

const TitleWithTwoLinksVariation: FC<{
  slice: CtaFeatureSliceTitleWithTwoLinks & CtaFeatureSlice;
}> = ({ slice }) => {
  const titleColor = slice?.primary?.title_color;
  const descriptionColor = slice?.primary?.description_color;

  return (
    <div className={styles.title_two_link_variation_body}>
      <div className={styles.info}>
        <PrismicRichText
          components={htmlSerializerWithProps({ color: titleColor })}
          field={slice?.primary?.title}
        />
        <PrismicRichText
          components={htmlSerializerWithProps({ color: descriptionColor })}
          field={slice?.primary?.description}
        />
      </div>
      <div className={styles.cta_section}>
        {renderCta(
          slice?.primary?.first_cta_link,
          slice?.primary?.first_cta_image,
        )}
        {renderCta(
          slice?.primary?.second_cta_link,
          slice?.primary?.second_cta_image,
        )}
      </div>
    </div>
  );
};

const ImageWithTitleAndButtonVariation: FC<{
  slice: CtaFeatureSliceImageWithTitleAndButton & CtaFeatureSlice;
}> = ({ slice }) => {
  const {
    title_color,
    image,
    cta_type,
    content_align,
    title,
    custom_cta_background,
    custom_cta_border_color,
    custom_cta_text_color,
    custom_cta_background_hover,
    custom_cta_border_color_hover,
    custom_cta_text_color_hover,
    external_link,
    internal_link,
    cta_button_text,
    fit_cta_button_width_to_content,
  } = slice?.primary ?? {};

  return (
    <div
      className={classNames(styles.image_title_button_variation_body, {
        [styles.image_title_button_variation_body__align_right]:
          content_align === 'right',
      })}
    >
      <div className={styles.image_container}>
        <figure className={styles.image_wrapper}>
          {<RenderImage image={image} />}
        </figure>
      </div>

      <div
        className={classNames(styles.content_section, {
          [styles.content_section__align_right]: content_align === 'right',
        })}
      >
        <PrismicRichText
          components={htmlSerializerWithProps({ color: title_color })}
          field={title}
        />
        <CTAButton
          className={styles.cta__button}
          ctaType={cta_type}
          customStyles={
            cta_type === 'custom'
              ? {
                  background: custom_cta_background,
                  border_color: custom_cta_border_color,
                  text_color: custom_cta_text_color,
                  background_hover: custom_cta_background_hover,
                  border_color_hover: custom_cta_border_color_hover,
                  text_color_hover: custom_cta_text_color_hover,
                }
              : undefined
          }
          external={external_link}
          fitContentWidth={fit_cta_button_width_to_content}
          link={internal_link ?? ''}
        >
          {cta_button_text ?? ''}
        </CTAButton>
      </div>
    </div>
  );
};

const ImageWithLogoAndButtonVariation: FC<{
  slice: CtaFeatureSliceImageWithLogoAndButton & CtaFeatureSlice;
}> = ({ slice }) => {
  const item = slice?.items?.[0];
  const ctaFitContentWidth = slice?.primary?.fit_cta_button_width_to_content;

  const primary = slice?.primary;
  const maxWidthHeader = item?.max_width_header
    ? `${item?.max_width_header}px`
    : '100%';
  const borderRadius = primary?.cta_full_width
    ? '0px'
    : `${primary?.image_border_radius}px`;

  const background = item?.content_background_color
    ? convertHexadecimalFormatToRGB(item?.content_background_color)
    : null;

  const logoSize = item?.logo_size ? `${item?.logo_size}px` : 80;

  const ctaBanner = (
    <>
      <figure
        className={classNames(styles.banner__horizontal, {
          [styles.banner__horizontal_not_placeholder]: item?.show_content,
        })}
        style={{
          borderRadius,
          paddingTop: getApectRatio(
            item?.image?.dimensions?.width as number,
            item?.image?.dimensions?.height as number,
            primary?.percentage_respect_width as number,
            false,
          ),
        }}
      >
        {<RenderImage borderRadius={borderRadius} fill image={item?.image} />}
      </figure>
      <figure
        className={classNames(styles.banner__vertical, {
          [styles.banner__vertical_not_placeholder]: item?.show_content,
        })}
        style={{
          paddingTop: getApectRatio(
            item?.image?.mobile?.dimensions?.width as number,
            item?.image?.mobile?.dimensions?.height as number,
            primary?.percentage_respect_to_width_mobile as number,
            true,
          ),
        }}
      >
        {<RenderImage borderRadius={'0px'} fill image={item?.image.mobile} />}
      </figure>
    </>
  );

  return (
    <>
      {item?.show_content && (
        <Container className={styles.info_container}>
          <div
            className={classNames(styles.info, {
              [styles.info__text_mobile_left_aligned]:
                item?.text_mobile_aligned_left,
              [styles.info_align_left]: item.content_align === 'left',
              [styles.info_align_right]: item.content_align === 'right',
              [styles.info_align_center]:
                item.content_align === 'center' || !item.content_align,
              [styles.info_use_cta_link]:
                !item?.cta_text && (item?.link || item?.external_link),
              [styles.info_full_width]: item?.max_width_header,
            })}
            style={{
              backgroundColor: item?.content_background_color
                ? `rgba(${background?.r}, ${background?.g}, ${background?.b}, ${
                    item?.content_background_opacity ?? 1
                  })`
                : undefined,
            }}
          >
            {item?.logo && (
              <div
                className={styles.logo_container}
                style={{ width: logoSize }}
              >
                <PrismicImageWithAlt
                  defaultAlt="Logo"
                  field={item.logo}
                  height={item?.logo?.dimensions?.height}
                  quality={100}
                  style={{
                    objectFit: 'contain',
                    width: '100%',
                    height: 'auto',
                  }}
                  width={item?.logo?.dimensions?.width}
                />
              </div>
            )}
            {renderTextSlice(
              item?.header,
              item?.header_color ?? '',
              maxWidthHeader,
            )}
            <div className={styles.cta_buttons_container}>
              {item?.cta_text && (
                <CTAButton
                  className={styles.cta__action_button}
                  ctaType={item?.cta_type}
                  customStyles={
                    item?.cta_type === 'custom'
                      ? {
                          background: item?.custom_cta_background,
                          border_color: item?.custom_cta_border_color,
                          text_color: item?.custom_cta_text_color,
                          background_hover: item?.custom_cta_background_hover,
                          border_color_hover:
                            item?.custom_cta_border_color_hover,
                          text_color_hover: item?.custom_cta_text_color_hover,
                        }
                      : undefined
                  }
                  external={item?.external_link}
                  fitContentWidth={ctaFitContentWidth}
                  link={item?.link ?? ''}
                >
                  {item?.cta_text}
                </CTAButton>
              )}
              {item?.secondary_cta_text && (
                <CTAButton
                  className={styles.cta__action_button}
                  ctaType={item?.secondary_cta_type}
                  customStyles={
                    item?.secondary_cta_type === 'custom'
                      ? {
                          background: item?.custom_secondary_cta_background,
                          border_color: item?.custom_secondary_cta_border_color,
                          text_color: item?.custom_secondary_cta_text_color,
                          background_hover: item?.custom_secondary_cta_bg_hover,
                          border_color_hover:
                            item?.custom_secondary_cta_border_hover,
                          text_color_hover:
                            item?.custom_secondary_cta_text_hover,
                        }
                      : undefined
                  }
                  external={item?.secondary_cta_external_link}
                  fitContentWidth={ctaFitContentWidth}
                  link={item?.secondary_cta_link ?? ''}
                >
                  {item?.secondary_cta_text}
                </CTAButton>
              )}
            </div>
          </div>
        </Container>
      )}
      {(item?.link || item?.external_link?.link_type === 'Any') && (
        <Link className={styles.cta__image_link} href={item?.link || '/'}>
          {ctaBanner}
          {item?.apply_effect_to_cta && item?.image?.url && (
            <div className={styles.cta__overlay} />
          )}
        </Link>
      )}
      {!item?.link && item?.external_link?.link_type !== 'Any' && (
        <PrismicLink
          className={styles.cta__image_link}
          field={item?.external_link}
        >
          {ctaBanner}
          {item?.apply_effect_to_cta && item?.image?.url && (
            <div className={styles.cta__overlay} />
          )}
        </PrismicLink>
      )}
    </>
  );
};

const getApectRatio = (
  width: number,
  height: number,
  percentage: number,
  mobile: boolean,
) => {
  if (
    !percentage ||
    (mobile && percentage <= 58.32) ||
    (!mobile && percentage <= 20.42)
  ) {
    return undefined;
  }

  const aspectRatioImage = (height / width) * 100;
  const aspectRatioPercentage = (percentage * aspectRatioImage) / 100;

  return `${aspectRatioPercentage}%`;
};

const RenderImage = ({
  image,
  borderRadius = '0px',
  fill,
}: {
  image: EmptyImageFieldImage | FilledImageFieldImage;
  borderRadius?: string;
  fill?: boolean;
}) => {
  if (!image?.url) {
    return <IconSvg name="image" />;
  }

  if (fill) {
    return (
      <PrismicImageWithAlt
        field={image}
        fill
        quality={100}
        style={{ borderRadius, objectFit: 'cover', objectPosition: 'center' }}
      />
    );
  }

  return (
    <PrismicImageWithAlt
      field={image}
      height={image?.dimensions?.height}
      quality={100}
      style={{
        borderRadius: borderRadius,
        objectFit: 'contain',
      }}
      width={image?.dimensions?.width}
    />
  );
};

const renderTextSlice = (text: any, color: string, maxWidth?: string) => {
  if (!prismicH.asText(text)) {
    return;
  }

  return (
    <PrismicRichText
      components={htmlSerializerWithProps({
        color: color,
        maxWidth: maxWidth,
      })}
      field={text}
    />
  );
};

const CTAButton: FC<ICtaButtonProps> = ({
  external,
  link,
  children,
  className,
  ctaType,
  customStyles,
  fitContentWidth,
}) => {
  const router = useRouter();

  const { isMobile } = useIsMobile(1024);

  if (!children) {
    return null;
  }

  const handleClic = (route?: string) => {
    if (!route) {
      return;
    }
    router.push(route);
  };

  // Generate inline styles based on customStyles
  const getCustomStyles = () => {
    if (!customStyles) return {};

    return {
      '--custom-bg': customStyles?.background || undefined,
      '--custom-border': customStyles?.border_color || undefined,
      '--custom-text': customStyles?.text_color || undefined,
      '--custom-bg-hover': customStyles?.background_hover || undefined,
      '--custom-border-hover': customStyles?.border_color_hover || undefined,
      '--custom-text-hover': customStyles?.text_color_hover || undefined,
    };
  };

  if (link || external?.link_type === 'Any') {
    return (
      <Button
        className={classNames(styles.cta__button, className, {
          [styles.cta__button_custom]: customStyles,
          [styles.cta__button_fit_content]: fitContentWidth,
        })}
        color={ctaType === 'custom' ? undefined : ctaType ?? 'primary'}
        onClick={() => handleClic(link)}
        size={isMobile ? 'sm' : 'md'}
        style={getCustomStyles()}
        type="button"
      >
        {children}
      </Button>
    );
  }

  return (
    <Link
      aria-label="open external link in new tab"
      className={classNames(styles.cta__link, className)}
      href={(external as any)?.url ?? '/'}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </Link>
  );
};

function renderCta(ctaLink, ctaImage) {
  if (!ctaImage?.url) {
    return null;
  }

  const image = (
    <PrismicImageWithAlt
      className={styles.cta_logo}
      defaultAlt="Logo"
      field={ctaImage}
      height={ctaImage?.dimensions?.height}
      width={ctaImage?.dimensions?.width}
    />
  );

  if (!ctaLink || !ctaLink?.url) {
    return (
      <PrismicLink aria-label="open external link in new tab" field={ctaLink}>
        {image}
      </PrismicLink>
    );
  }

  const href = prismicH.asLink(ctaLink) || '/';

  return (
    <Link aria-label="open external link in new tab" href={href}>
      {image}
    </Link>
  );
}

interface ICtaButtonProps {
  link?: string;
  className?: string;
  children?: ReactNode;
  external?: LinkField;
  ctaType?: 'link' | 'primary' | 'secondary' | 'tertiary' | 'custom';
  fitContentWidth?: boolean;
  customStyles?: {
    background?: ColorField;
    border_color?: ColorField;
    text_color?: ColorField;
    background_hover?: ColorField;
    border_color_hover?: ColorField;
    text_color_hover?: ColorField;
  };
}

export { RenderCta };
